import { QMenuButton, QMenuItem } from '@qualio/ui-components';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '../../../../../api/model/tag';
import { MedtechUserV2 } from '../../../../../api/user';
import { EditorMode } from '../../../../../util/EditModeUtils';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../Context';
import { useIsNarrowEditorViewport } from '../../hooks';
import { ReviewButton } from '../ReviewButton';
import { SummarizeButton } from '../SummarizeButton';
import { ActivityReportAction } from './ActivityReportAction';
import { ChangeFormatButton } from './ChangeFormatButton';
import { ChangeOwnerButton } from './ChangeOwnerButton';
import { DeleteDocumentAction } from './DeleteDocumentAction';
import { HistoryAction } from './HistoryAction';
import './MoreActions.module.css';
import { RegeneratePreviewButton } from './RegeneratePreviewButton';
import { ResetEditorSessionButton } from './ResetEditorSessionButton';
import { RetireAction } from './RetireAction';

const renderQMenuItem = (
  onClick: () => void,
  key: string,
  testId: string,
  label: string,
  isDisabled?: boolean,
) => {
  return (
    <QMenuItem
      key={key}
      data-cy={testId}
      onClick={onClick}
      fontSize="sm"
      isDisabled={isDisabled}
    >
      {label}
    </QMenuItem>
  );
};

const renderDeleteItem = (onClick: () => void, t: any) => {
  return renderQMenuItem(
    onClick,
    'delete-doc',
    'doc-overview-kebab-menu-delete',
    t('buttons.delete'),
  );
};

const renderHistoryItem = (onClick: () => void, t: any) => {
  return renderQMenuItem(
    onClick,
    'history-doc',
    'doc-overview-kebab-menu-history',
    t('labels.history'),
  );
};

const renderRetireDocItem = (onClick: () => void, t: any) => {
  return renderQMenuItem(
    onClick,
    'retire-doc',
    'doc-overview-kebab-menu-retire',
    t('buttons.retire'),
  );
};

const renderChangeOwnerButton = (onClick: () => void, t: any) => {
  return renderQMenuItem(
    onClick,
    'change-owner',
    'doc-overview-kebab-menu-change-owner',
    t('buttons.changeOwner'),
  );
};

const renderResetEditorSessionButton = (onClick: () => void, t: any) => {
  return renderQMenuItem(
    onClick,
    'reset-editor',
    'doc-overview-kebab-menu-reset-editor',
    t('buttons.resetEditorSession'),
  );
};

const renderRegeneratePreviewButton = (
  onClick: () => void,
  t: any,
  isDisabled: boolean,
) => {
  return renderQMenuItem(
    onClick,
    'regenerate-preview',
    'doc-overview-regenerate-preview',
    t('buttons.regeneratePreview'),
    isDisabled,
  );
};

const renderChangeFormatButton = (onClick: () => void, t: any) => {
  return renderQMenuItem(
    onClick,
    'change-format',
    'doc-overview-kebab-menu-change-format',
    t('buttons.changeFormat'),
  );
};

const renderActivityReportAction = (onClick: () => void, label: string) => {
  return renderQMenuItem(
    onClick,
    'activity-report',
    'doc-overview-kebab-menu-activity-report',
    label,
  );
};

type MoreActionsButtonProps = {
  users: MedtechUserV2[];
  tags: Tag[];
};

export const MoreActionsButton = ({ users, tags }: MoreActionsButtonProps) => {
  const { t } = useTranslation();
  const { currentEditorMode, qualioDocument } = useContext(
    DocumentOverviewContext,
  );
  const { isDisabled } = useContext(ButtonGroupContext);
  const isNarrowViewport = useIsNarrowEditorViewport();

  if (currentEditorMode !== EditorMode.VIEW) {
    return null;
  }
  return (
    <div className="MoreActions__kebab-menu-wrapper">
      <QMenuButton
        buttonLabel={t('buttons.options')}
        data-cy="doc-overview-kebab-menu"
        iconName="MoreVertical"
        itemSize="sm"
        variant="icon"
        isDisabled={isDisabled}
      >
        {isNarrowViewport && <ReviewButton renderAs="menuItem" />}
        {isNarrowViewport && (
          <SummarizeButton
            renderAs="menuItem"
            documentCode={qualioDocument.code}
          />
        )}
        <ChangeOwnerButton
          renderClickableItem={(onClick) => renderChangeOwnerButton(onClick, t)}
          users={users}
          tags={tags}
        />
        <ChangeFormatButton
          renderClickableItem={(onClick) =>
            renderChangeFormatButton(onClick, t)
          }
        />
        <ActivityReportAction
          renderClickableItem={renderActivityReportAction}
        />
        <HistoryAction
          renderClickableItem={(onClick) => renderHistoryItem(onClick, t)}
        />
        <ResetEditorSessionButton
          renderClickableItem={(onClick) =>
            renderResetEditorSessionButton(onClick, t)
          }
        />
        <RegeneratePreviewButton
          renderClickableItem={(onClick, isDisabled) =>
            renderRegeneratePreviewButton(onClick, t, isDisabled)
          }
        />
        <RetireAction
          renderClickableItem={(onClick) => renderRetireDocItem(onClick, t)}
        />
        <DeleteDocumentAction
          renderClickableItem={(onClick) => renderDeleteItem(onClick, t)}
        />
      </QMenuButton>
    </div>
  );
};
